import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { FlagIcon } from "@phosphor-icons/react/dist/csr/Flag";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { ThumbsDownIcon } from "@phosphor-icons/react/dist/csr/ThumbsDown";
import { ThumbsUpIcon } from "@phosphor-icons/react/dist/csr/ThumbsUp";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import type { ReactNode } from "react";
import { LogoIcon } from "@volca/ui/components/ui/logo";
import { formatReportPeriod } from "@/profitability/utils";
import { guessPhrase, REASON_LABEL, RESOLUTION_LABEL } from "./labels";
import type {
  FindingActivity,
  FindingHypothesis,
  FindingResolution,
} from "./types";

/** The model's mark in the feed: Volca's own logo, never a sparkle. */
export const VolcaMark = () => (
  <span className="flex size-5 items-center justify-center">
    <LogoIcon width={14} height={12} />
  </span>
);

type Tone = "neutral" | "ai" | "success" | "warning" | "danger";

const text = (value: unknown) => (typeof value === "string" ? value : null);

const periodOf = (data: FindingActivity["data"]) => {
  const start = text(data.start_date);
  const end = text(data.end_date);
  return start && end
    ? ` in ${formatReportPeriod({ start_date: start, end_date: end })}`
    : "";
};

const resolutionOf = (data: FindingActivity["data"]) => {
  const resolution = text(data.resolution) as FindingResolution | null;
  return resolution ? RESOLUTION_LABEL[resolution].toLowerCase() : null;
};

/** What one event says: its marker, the line after the actor, and any body. */
export const describe = (
  activity: FindingActivity,
  /**
   * The finding's own reading, for the name of a cause its report declared
   * when the event itself did not keep one.
   */
  hypothesis?: FindingHypothesis | null,
): { tone: Tone; icon: ReactNode; line: string; body?: string | null } => {
  const { data } = activity;
  switch (activity.kind) {
    case "finding.flagged":
      return {
        tone: "warning",
        icon: <FlagIcon />,
        line: `flagged it${periodOf(data)}`,
        body: text(data.flag),
      };
    case "finding.flagged_again":
      return {
        tone: "neutral",
        icon: <FlagIcon />,
        line: `flagged it again${periodOf(data)}`,
        body: text(data.flag),
      };
    case "finding.reopened":
      return {
        tone: "neutral",
        icon: <ArrowCounterClockwiseIcon />,
        line: activity.actor
          ? "reopened it"
          : data.reason === "grew"
            ? `opened it again: it grew${periodOf(data)}`
            : `opened it again for a new reason${periodOf(data)}`,
        body: text(data.flag),
      };
    case "finding.explained":
      return {
        tone: "neutral",
        icon: <XIcon />,
        line: `explained it${resolutionOf(data) ? `: ${resolutionOf(data)}` : ""}`,
        body: text(data.note),
      };
    case "finding.investigating":
      return {
        tone: "warning",
        icon: <MagnifyingGlassIcon />,
        line: "started looking into it",
      };
    case "finding.action_required":
      return {
        tone: "danger",
        icon: <WarningIcon />,
        line: "marked it as needing action",
        body: text(data.note),
      };
    case "finding.resolved":
      return {
        tone: "success",
        icon: <CheckIcon />,
        line:
          data.reason === "no_longer_flagged"
            ? "resolved it: no longer flagged in the rerun"
            : "marked it fixed",
        body: text(data.note),
      };
    case "finding.hypothesis": {
      const cause = text(data.cause);
      const confidence =
        typeof data.confidence === "number" ? data.confidence : undefined;
      // A script's cause reads by its name, never as its key.
      const name =
        text(data.cause_name) ??
        (hypothesis?.cause === cause ? hypothesis?.cause_name : null);
      return {
        tone: "neutral",
        icon: <VolcaMark />,
        line: cause
          ? `guessed: ${guessPhrase(cause, confidence, name).toLowerCase()}`
          : "could not guess a cause",
      };
    }
    case "feedback.up":
      return {
        tone: "neutral",
        icon: <ThumbsUpIcon />,
        line: "rated it worth a look",
      };
    case "feedback.down": {
      const reason = text(data.reason);
      return {
        tone: "neutral",
        icon: <ThumbsDownIcon />,
        line: `rated it not worth a look${reason ? `: ${(REASON_LABEL[reason] ?? reason).toLowerCase()}` : ""}`,
        body: text(data.note),
      };
    }
    case "feedback.cleared":
      return {
        tone: "neutral",
        icon: <ThumbsUpIcon />,
        line: "took back their rating",
      };
    default:
      return {
        tone: "neutral",
        icon: <ChatCircleIcon />,
        line: "added a note",
        body: text(data.body),
      };
  }
};

export const actorName = (activity: FindingActivity) =>
  activity.actor?.full_name ??
  (activity.actor_kind === "ai" ? "Volca" : "The report");
